/**
 * OverviewTab Component
 *
 * Fetches BFF overview and location data on mount and on primary filter change,
 * and renders the fault tree dendrogram across the full canvas. The timeline
 * sparkline and the map live in the slim top bar (OverviewBarControls); when the
 * timeline drill-down sets secondaryDateFilter, this tab re-fetches node badges so
 * the dendrogram reflects the drilled-down date range.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import FaultTreeDendrogram from '../FaultTreeDendrogram';
import FullscreenWrapper from '../../../../../../common/FullscreenWrapper';
import {FaultTreeNodeBadgeDTO} from '../../../../../../../@types/integrated-risk-picture';
import {
    useIRPWorkbookStore,
    useIRPWorkbookStoreApi,
    useOverviewCache,
    usePrimaryFilter,
    useSecondaryDateFilter,
} from '../../../../../../../stores/irp-workbook.store';
import {useIRPContext} from '../../IRPWorkbookProvider';
import {toast} from 'react-toastify';

/**
 * Overview tab — fault tree dendrogram across the full canvas.
 */
const OverviewTab: React.FC = () => {
    const irpId = useIRPWorkbookStore((state) => state.irpId);
    const {dataSource, componentMapping} = useIRPContext();
    const primaryFilter = usePrimaryFilter();
    const overviewCache = useOverviewCache();
    const secondaryDateFilter = useSecondaryDateFilter();

    const setOverviewCache = useIRPWorkbookStore((state) => state.setOverviewCache);
    const setReportLocationsCache = useIRPWorkbookStore((state) => state.setReportLocationsCache);
    const setInitialDateRange = useIRPWorkbookStore((state) => state.setInitialDateRange);
    const setAvailableSources = useIRPWorkbookStore((state) => state.setAvailableSources);
    const storeApi = useIRPWorkbookStoreApi();

    // Node badges filtered to the secondary date range; null when no secondary filter is active.
    const [secondaryNodeBadges, setSecondaryNodeBadges] = useState<FaultTreeNodeBadgeDTO[] | null>(null);

    // Guard: only pre-populate date pickers from overview data once per IRP load.
    // Resets to false when irpId changes (effect runs before the fetch effect).
    const hasSetInitialDatesRef = useRef(false);
    useEffect(() => {
        hasSetInitialDatesRef.current = false;
    }, [irpId]);

    // Fetch overview and locations on irpId / primaryFilter change.
    // Caches are intentionally NOT in the dependency array — they are cleared atomically
    // with the irpId / primaryFilter change (via initializeWorkbook / setPrimaryFilter),
    // so adding them would cause the effect to re-fire when one fetch response arrives
    // before the other, triggering a duplicate fetch for the slower endpoint (Issue 5).
    // Instead, we read current cache state via getState() to guard against redundant calls.
    useEffect(() => {
        if (!irpId || !dataSource) return;

        const state = storeApi.getState();
        const needsOverview = state.overviewCache === null;
        const needsLocations = state.reportLocationsCache === null;
        if (!needsOverview && !needsLocations) return;

        const { dateFrom, dateTo, selectedDescriptors, selectedSources } = primaryFilter;
        const dti = selectedDescriptors.size > 0 ? Array.from(selectedDescriptors) : undefined;
        const sn = selectedSources.size > 0 ? Array.from(selectedSources) : undefined;

        const pending: Array<Promise<void>> = [];

        if (needsOverview) {
            pending.push(
                dataSource.getWorkbookOverview(dateFrom, dateTo, dti, sn)
                    .then((overview) => {
                        setOverviewCache(overview);
                        setAvailableSources(overview.reportCountBySource.map((s) => s.sourceName));
                        if (!hasSetInitialDatesRef.current
                            && !dateFrom && !dateTo
                            && overview.earliestReportDate && overview.latestReportDate) {
                            hasSetInitialDatesRef.current = true;
                            setInitialDateRange(
                                new Date(overview.earliestReportDate),
                                new Date(overview.latestReportDate)
                            );
                        }
                    })
            );
        }

        if (needsLocations) {
            pending.push(
                dataSource.getWorkbookReportLocations(dateFrom, dateTo, dti, sn)
                    .then(setReportLocationsCache)
            );
        }

        Promise.all(pending).catch(() => {
            toast.error('Failed to load workbook overview data.');
        });
    }, [irpId, dataSource, primaryFilter, setInitialDateRange, storeApi]); // cache refs intentionally omitted, see comment above

    // Re-fetch node badges from the server when secondaryDateFilter changes so the dendrogram
    // reflects the drilled-down date range. Uses the same overview endpoint — no extra API needed.
    useEffect(() => {
        if (!irpId || !dataSource) return;

        // A component's mapping endpoints take no date window: every report from the source is in
        // scope (specification 0.4, section 4.1). Re-fetching here would spend a request to get
        // back exactly the unfiltered overview and then present it as a drill-down.
        if (componentMapping) {
            setSecondaryNodeBadges(null);
            return;
        }

        if (!secondaryDateFilter) {
            setSecondaryNodeBadges(null);
            return;
        }

        const { selectedDescriptors, selectedSources } = primaryFilter;
        const dti = selectedDescriptors.size > 0 ? Array.from(selectedDescriptors) : undefined;
        const sn = selectedSources.size > 0 ? Array.from(selectedSources) : undefined;

        dataSource.getWorkbookOverview(secondaryDateFilter.dateFrom, secondaryDateFilter.dateTo, dti, sn)
            .then((overview) => {
                setSecondaryNodeBadges(overview.nodeBadges);
            })
            .catch(() => {
                toast.error('Failed to load filtered node badges.');
            });
    }, [irpId, dataSource, primaryFilter, secondaryDateFilter, componentMapping]);

    // Stale-while-revalidate for dendrogram: keep the last known badges so the dendrogram
    // never blanks out during a re-fetch. FaultTreeDendrogram handles its own loading states.
    const activeBadges = secondaryNodeBadges ?? overviewCache?.nodeBadges;
    const lastBadgesRef = useRef<FaultTreeNodeBadgeDTO[] | undefined>(undefined);
    if (activeBadges !== undefined) {
        lastBadgesRef.current = activeBadges;
    }

    const overviewLoading = overviewCache === null && !!irpId;

    return (
        <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <FullscreenWrapper fillParent>
                <FaultTreeDendrogram
                    nodeBadges={lastBadgesRef.current}
                    isLoading={overviewLoading}
                />
            </FullscreenWrapper>
        </Box>
    );
};

export default OverviewTab;
